import { groupBy } from "lodash";

import { SpendOverTimePoint } from "lib/hooks/api/spend/useSpendOverTime";
import { SpendBuyerTypePoint } from "lib/hooks/api/spend/useSpendOverTimeByBuyerType";
import type { SpendRankingSort } from "./hooks/useRankingTable";
import { SpendMeasure } from "./hooks/useSpendUrlState";
import { Dataset } from "./SpendChart";

/**
 * Shapes grouped `spendOverTime` points into `SpendChart` datasets: one dataset
 * per group (buyer or supplier), carrying only the periods that group actually
 * has spend in. `label` is the hydrated group name; `id` is the group key
 * (buyer/supplier GUID) when present, else the name.
 *
 * Gaps are filled by the chart itself, which walks the whole calendar range and
 * so also covers periods absent from the response entirely.
 *
 * Shared by `BuyerChart` and `SupplierChart`, which differ only in which
 * `spendOverTime` grouping they request.
 */
export function buildGroupedSpendDatasets(
  points: SpendOverTimePoint[],
  measure: SpendMeasure,
): Dataset[] {
  const grouped = groupBy(points, (p) => p.groupName ?? p.groupKey ?? "");

  return Object.entries(grouped).map(([label, groupPoints]) => ({
    label,
    id: groupPoints[0]?.groupKey ?? label,
    data: groupPoints.map((p) => ({
      date: p.period,
      total: p[measure],
    })),
  }));
}

/**
 * The buyer type series in the shape of a grouped `spendOverTime` one, keyed by the
 * type (e.g. `HEALTHCARE`, or `NONE` for no type), so the grouped chart and ranking
 * helpers serve it unchanged.
 */
export function buyerTypePointsAsGrouped(points: SpendBuyerTypePoint[]): SpendOverTimePoint[] {
  return points.map((point) => ({
    groupKey: point.buyerType,
    groupName: point.name,
    period: point.period,
    totalValue: point.totalValue,
    transactionCount: point.transactionCount,
  }));
}

/** One group's totals across every period of a grouped `spendOverTime` series. */
export type GroupedSpendTotal = {
  key: string;
  name: string;
  totalValue: number;
  transactionCount: number;
};

/**
 * Sums a grouped `spendOverTime` series into one row per group, ordered by the ranking's
 * sort. For a series with few enough groups to rank in full in the browser, such as
 * buyer types, so the ranking is the same data the chart plots.
 */
export function rankGroupedSpend(
  points: SpendOverTimePoint[],
  sort: SpendRankingSort,
): GroupedSpendTotal[] {
  const totals = new Map<string, GroupedSpendTotal>();
  for (const point of points) {
    const key = point.groupKey ?? point.groupName ?? "";
    const total = totals.get(key) ?? {
      key,
      name: point.groupName ?? key,
      totalValue: 0,
      transactionCount: 0,
    };
    total.totalValue += point.totalValue;
    total.transactionCount += point.transactionCount;
    totals.set(key, total);
  }
  const direction = sort.sortOrder === "ASC" ? 1 : -1;
  return [...totals.values()].sort((a, b) => direction * (a[sort.measure] - b[sort.measure]));
}
